import React, { useEffect } from 'react'
import authService from '@/services/authService'

/**
 * /start - a one-shot "reset" entry point we can hand to customers
 * (and the support team) when someone is stuck on a stale version of
 * the portal after a deploy (e.g. an open tab still running the old
 * bundle, so it shows wrong delivery/pause state).
 *
 * It wipes everything for this origin that JavaScript can actually
 * reach (localStorage, sessionStorage, the Cache Storage API, and any
 * registered service worker), then does a FULL document navigation to
 * /login. The full navigation is the part that matters: index.html is
 * served no-store and our bundles are content-hashed, so re-fetching
 * index.html always pulls the newest code. That is what clears the
 * "stale version" symptom.
 *
 * Deliberate non-goals:
 *  - We can't clear the browser's HTTP disk cache from JS (no API
 *    exists), and we don't need to: the no-store index + hashed
 *    bundles guarantee a full reload loads the latest build.
 *  - We do NOT clear cookies, so the user's cookie-consent choice is
 *    preserved and they aren't re-prompted.
 *
 * Reach this as a fresh page load (typing/opening the URL), not an
 * in-app link: an old already-loaded bundle has no /start route and
 * would just 404 client-side without resetting anything.
 */
export default function StartReset(): React.JSX.Element {
  useEffect(() => {
    const reset = async (): Promise<void> => {
      // Revoke the auth token SERVER-SIDE first (BUG-9 hygiene, backend
      // docs/bugs.md): clearing storage alone would orphan a still-valid
      // token on the server. Must run before the storage wipe below, which
      // deletes the token logout needs. Best-effort: offline or expired is
      // fine, the wipe still proceeds.
      try {
        await authService.logout()
      } catch {
        /* best-effort */
      }

      try {
        localStorage.clear()
      } catch {
        /* storage may be unavailable (private mode / blocked) */
      }
      try {
        sessionStorage.clear()
      } catch {
        /* ignore */
      }

      // Cache Storage API: empty today, but clear it defensively so an
      // old service worker (e.g. an agency-era build) can't keep
      // serving a stale shell.
      try {
        if ('caches' in window) {
          const keys = await caches.keys()
          await Promise.allSettled(keys.map((key) => caches.delete(key)))
        }
      } catch {
        /* ignore */
      }

      // Unregister any service workers registered for this origin.
      try {
        if ('serviceWorker' in navigator) {
          const registrations =
            await navigator.serviceWorker.getRegistrations()
          await Promise.allSettled(
            registrations.map((registration) => registration.unregister()),
          )
        }
      } catch {
        /* ignore */
      }

      // Full-page navigation (NOT react-router) so the browser
      // re-fetches the no-store index.html and loads the newest
      // bundle. The cache-busting param is belt-and-braces on top of
      // the no-store header.
      const stamp = Date.now()
      window.location.replace(`/login?fresh=${stamp}`)
    }

    void reset()
  }, [])

  return (
    <div className="auth-form-container">
      <div className="auth-form-card">
        <p className="auth-welcome">Resetting the portal, one moment...</p>
      </div>
    </div>
  )
}
